/**
 * The home screen's search and filter (Task 4) — her words: *"Add a search
 * feature to the navigation pane. Users should be able to search by building
 * name, due date, bid date, and survey status."*
 *
 * Pure over `SurveySummary[]`, the same contract `surveyList.ts` keeps: no
 * store access, no engine call, and — deliberately — no `Date.now()` either.
 * The two date facets are RANGES the surveyor TYPED (Ruling 10 — "Due
 * from"/"Due to", "Bid due from"/"Bid due to"), not a comparison against
 * today, so this module never reads a clock and stays as trivially testable
 * as everything else in `src/ui/state`.
 *
 * `HomeScreen` runs this once per group (assigned / field-initiated /
 * submitted) rather than once over the flat record list, so a filtered list
 * keeps the same recency order `groupSurveys` already gave it and the group
 * eyebrows keep meaning what they meant before a filter existed.
 */

import type { SurveySummary, TagKind } from './surveyList';

export interface SurveyFilter {
  /** Free text, matched case-insensitively as a substring of `title`. */
  text: string;
  /** Which status words to keep. Empty means every status passes. */
  status: readonly TagKind[];
  /** ISO `YYYY-MM-DD`. Keeps rows whose `dueDate` is on or after this date. */
  dueFrom?: string;
  /** ISO `YYYY-MM-DD`. Keeps rows whose `dueDate` is on or before this date. */
  dueTo?: string;
  /** ISO `YYYY-MM-DD`. Keeps rows whose `bidDate` is on or after this date. */
  bidFrom?: string;
  /** ISO `YYYY-MM-DD`. Keeps rows whose `bidDate` is on or before this date. */
  bidTo?: string;
}

/** No facet active — the funnel's resting state, and `filterSurveys`' no-op. */
export const EMPTY_FILTER: SurveyFilter = { text: '', status: [] };

function matchesText(summary: SurveySummary, text: string): boolean {
  const needle = text.trim().toLowerCase();
  return needle === '' || summary.title.toLowerCase().includes(needle);
}

function matchesStatus(summary: SurveySummary, status: readonly TagKind[]): boolean {
  return status.length === 0 || status.includes(summary.tagKind);
}

/**
 * Ruling 10 — every date facet is a RANGE, not a single bound. Both `from`
 * and `to` are optional and inclusive; ISO `YYYY-MM-DD` strings compare
 * correctly with `<`/`<=` (lexicographic order matches calendar order for a
 * fixed-width, zero-padded date), so no `Date` is ever constructed.
 *
 * An inverted range (`from > to`) matches nothing — checked before `value`
 * is even looked at, so it holds for an unset date too. No swap, no message:
 * the surveyor typed the bounds backwards and the empty list is the tell.
 *
 * With neither bound active, every row passes (the facet is off). With
 * either bound active, an unset `value` never matches — an unanswered date
 * cannot fall inside a range the surveyor asked for.
 */
function within(value: string, from: string | undefined, to: string | undefined): boolean {
  if (from === undefined && to === undefined) return true;
  if (from !== undefined && to !== undefined && from > to) return false;
  if (value === '') return false;
  if (from !== undefined && value < from) return false;
  if (to !== undefined && value > to) return false;
  return true;
}

/**
 * The whole filter, applied once. An empty filter (every facet at its rest
 * value) keeps every summary — the brief's own words, "an empty filter
 * returns the input unchanged" — because each `matches*` above is
 * unconditionally true at that facet's rest value; there is no separate
 * short-circuit to keep in sync with the facets above.
 */
export function filterSurveys(
  summaries: readonly SurveySummary[],
  filter: SurveyFilter,
): SurveySummary[] {
  return summaries.filter(
    (summary) =>
      matchesText(summary, filter.text) &&
      matchesStatus(summary, filter.status) &&
      within(summary.dueDate, filter.dueFrom, filter.dueTo) &&
      within(summary.bidDate, filter.bidFrom, filter.bidTo),
  );
}

/**
 * How many of the filter's four facets are actively narrowing the list — the
 * funnel badge's number. Status counts as ONE facet regardless of how many
 * boxes are checked, and a date RANGE counts as ONE facet regardless of
 * whether one or both of its bounds are set (Ruling 10): the badge answers
 * "how many kinds of narrowing are on", not "how many values are selected".
 */
export function filterCount(filter: SurveyFilter): number {
  let count = 0;
  if (filter.text.trim() !== '') count += 1;
  if (filter.status.length > 0) count += 1;
  if (filter.dueFrom !== undefined || filter.dueTo !== undefined) count += 1;
  if (filter.bidFrom !== undefined || filter.bidTo !== undefined) count += 1;
  return count;
}
